import { useEffect, useState, useCallback, useRef } from 'react';
import { withKey, API_BASE } from '../services/utils';
import { isRowNonEmpty } from './useOfferState';

const normalizeSearchText = (value) =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Starter products inserted on new offers, in the order they should appear.
//
// Looked up by CODE, not by hardcoded Zoho id. The Products module has been
// re-created at least once (every id moved from 86998800000215xxxx to
// 86998800000547xxxx), which silently killed the previous hardcoded ids: new
// offers stopped getting these rows at all, and the copy-from-offer guard that
// used the same id set stopped matching. Same failure already recorded for
// OKORREKT in OfferBuilder.js. Codes are stable, ids are not.
export const DEFAULT_PRODUCT_CODES = ['OKORREKT', 'OMARK', 'OFBH'];

// True for a row holding one of the starter products. Matches on `product`,
// which carries the product code — the same test OfferBuilder uses for OKORREKT.
export const isStarterProductRow = (item) =>
  DEFAULT_PRODUCT_CODES.includes(String(item?.product || '').trim().toUpperCase());

// True for a starter row the user has not adjusted — a starter row counts as
// "no real work yet" for both the copy-from-offer flow (OfferBuilder.js) and
// the initial insertion effect below, so either one can tell an untouched
// starter row apart from one the user has actually started pricing. Only
// price and quantity are checked; description and supplier arrive pre-filled.
export const isUntouchedStarterRow = (item) => {
  if (!isStarterProductRow(item)) return false;
  const qty = parseFloat(item?.quantity);
  if (!Number.isNaN(qty) && qty !== 1) return false;
  if ((item?.unit_cost_expr || '').trim()) return false;
  if ((item?.unit_price_expr || '').trim()) return false;
  if (item?.unit_price_locked) return false;
  return true;
};

// Resolve one product code against the cache. Returns null when the cache has
// no exact match, so callers can skip rather than insert a wrong product.
export async function fetchProductByCode(code) {
  const response = await fetch(await withKey(`${API_BASE}/api/zoho/products?search=${encodeURIComponent(code)}`));
  const data = await response.json();
  return (data.products || []).find(
    p => String(p.code || '').trim().toUpperCase() === code.toUpperCase()
  ) || null;
}

export function useProductsVendors({ offer, setOffer, emptyItem }) {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState({});
  const [showVendorDropdown, setShowVendorDropdown] = useState({});
  const [searchingProduct, setSearchingProduct] = useState({});
  const [searchingVendor, setSearchingVendor] = useState({});
  const [allVendors, setAllVendors] = useState([]);
  const [vendorsProductId, setVendorsProductId] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [pendingSupplierQueue, setPendingSupplierQueue] = useState([]);
  const pendingProcessedRef = useRef(null);

  const fetchVendorsForProduct = async (itemIndex, suppressDropdown = false) => {
    const productId = offer.items[itemIndex]?.product_id;

    if (vendorsProductId === productId && allVendors.length > 0) {
      setVendors(allVendors);
      if (!suppressDropdown) setShowVendorDropdown(prev => ({ ...prev, [itemIndex]: true }));
      return;
    }

    setSearchingVendor(prev => ({ ...prev, [itemIndex]: true }));
    try {
      let fetchedVendors = [];
      if (productId) {
        const response = await fetch(await withKey(`${API_BASE}/api/zoho/product_suppliers?product_id=${encodeURIComponent(productId)}`));
        const data = await response.json();
        const seen = new Set();
        for (const s of (data.suppliers || [])) {
          if (!seen.has(s.id)) {
            fetchedVendors.push(s);
            seen.add(s.id);
          }
        }
      } else {
        const response = await fetch(await withKey(`${API_BASE}/api/zoho/vendors`));
        const data = await response.json();
        fetchedVendors = data.vendors || [];
      }
      setAllVendors(fetchedVendors);
      setVendors(fetchedVendors);
      setVendorsProductId(productId);
      if (!suppressDropdown) setShowVendorDropdown(prev => ({ ...prev, [itemIndex]: true }));
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
    }
    setSearchingVendor(prev => ({ ...prev, [itemIndex]: false }));
  };

  const filterVendors = (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      setVendors(allVendors);
      return;
    }
    const lower = normalizeSearchText(searchTerm);
    const filtered = allVendors.filter(vendor =>
      (vendor.name && normalizeSearchText(vendor.name).includes(lower)) ||
      (vendor.supplier_code && normalizeSearchText(vendor.supplier_code).includes(lower)) ||
      (vendor.email && normalizeSearchText(vendor.email).includes(lower))
    );
    setVendors(filtered);
  };

  const selectVendor = (vendor, index) => {
    const newItems = [...offer.items];
    newItems[index] = {
      ...newItems[index],
      supplier_name: vendor.supplier_code || vendor.name,
      supplier_id: vendor.id
    };
    setOffer({ ...offer, items: newItems });
    setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
    setVendors([]);
    // Advance pending supplier queue if this was the current pending row
    setPendingSupplierQueue(prev => {
      if (prev.length > 0 && prev[0].rowIndex === index) {
        return prev.slice(1);
      }
      return prev;
    });
  };

  const advanceSupplierQueue = useCallback((index) => {
    setPendingSupplierQueue(prev => {
      if (prev.length > 0 && prev[0].rowIndex === index) {
        return prev.slice(1);
      }
      return prev;
    });
  }, []);

  const fetchAllProducts = async (itemIndex, suppressDropdown = false) => {
    if (productsLoaded && allProducts.length > 0) {
      setProducts(allProducts);
      if (!suppressDropdown) setShowProductDropdown(prev => ({ ...prev, [itemIndex]: true }));
      return;
    }
    if (!suppressDropdown) setShowProductDropdown(prev => ({ ...prev, [itemIndex]: true }));
    setSearchingProduct(prev => ({ ...prev, [itemIndex]: true }));
    try {
      const response = await fetch(await withKey(`${API_BASE}/api/zoho/products?search=`));
      const data = await response.json();
      // This is the whole catalogue, not the result of a search, so no entry is
      // a "match" for anything the user typed. Mark it so Tab/Enter will not
      // auto-commit the first row — otherwise opening the full list (double
      // click, or Enter on a closed dropdown) and tabbing on stamps whatever
      // sorts first, which is the product literally named 80DAYS.
      const productsList = (data.products || []).map(p => ({ ...p, unfiltered_listing: true }));
      setAllProducts(productsList);
      setProducts(productsList);
      setProductsLoaded(true);
      if (!suppressDropdown) setShowProductDropdown(prev => ({ ...prev, [itemIndex]: true }));
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      if (!suppressDropdown) setShowProductDropdown(prev => ({ ...prev, [itemIndex]: true }));
    }
    setSearchingProduct(prev => ({ ...prev, [itemIndex]: false }));
  };

  const filterProducts = async (searchTerm) => {
    if (!searchTerm) {
      setProducts(allProducts);
      return;
    }
    try {
      const response = await fetch(await withKey(`${API_BASE}/api/zoho/products?search=${encodeURIComponent(searchTerm)}`));
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      // Fall back to client-side filter
      const lower = normalizeSearchText(searchTerm);
      const filtered = allProducts.filter(p =>
        (p.code && normalizeSearchText(p.code).includes(lower)) ||
        (p.name && normalizeSearchText(p.name).includes(lower)) ||
        (p.description && normalizeSearchText(p.description).includes(lower))
      );
      setProducts(filtered);
    }
  };

  const selectProduct = async (product, index) => {
    console.log('[selectProduct] product data:', JSON.stringify(product, null, 2));
    const productPrice = Number(product.unit_price || 0);
    const productVat = !!product.vat;
    const newItems = [...offer.items];
    newItems[index] = {
      ...newItems[index],
      product: product.Product_Code || product.name,
      product_id: product.id,
      description: product.description,
      unit_cost: productPrice || '',
      unit_price: productPrice || '',
      vat: productVat ? 1 : 0,
      hidden: !!product.hidden,
      product_hidden: !!product.hidden,
      total: newItems[index].quantity * productPrice,
      supplier_name: '',
      supplier_id: null
    };
    setOffer({ ...offer, items: newItems });
    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
    setProducts(allProducts);

    try {
      const response = await fetch(await withKey(`${API_BASE}/api/zoho/product_suppliers?product_id=${encodeURIComponent(product.id)}`));
      const data = await response.json();
      const suppliers = data.suppliers || [];
      console.log('[selectProduct] suppliers for product:', JSON.stringify(suppliers, null, 2));

      setAllVendors(suppliers);
      setVendorsProductId(product.id);

      // If product code starts with "F", auto-select OCPHBILL (Billetkontoret) if available
      const productCode = (product.code || product.Product_Code || '').trim();
      let autoSupplier = null;
      if (productCode.toUpperCase().startsWith('F') && suppliers.length > 0) {
        autoSupplier = suppliers.find(s =>
          (s.supplier_code || '').toUpperCase() === 'OCPHBILL' ||
          (s.name || '').toLowerCase() === 'billetkontoret'
        ) || null;
      }

      if (autoSupplier) {
        const updatedItems = [...newItems];
        updatedItems[index].supplier_name = autoSupplier.supplier_code || autoSupplier.name;
        updatedItems[index].supplier_id = autoSupplier.id;
        setOffer(prev => ({ ...prev, items: updatedItems }));
        setVendors([]);
        setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
      } else if (suppliers.length === 1) {
        const singleSupplier = suppliers[0];
        const updatedItems = [...newItems];
        updatedItems[index].supplier_name = singleSupplier.supplier_code || singleSupplier.name;
        updatedItems[index].supplier_id = singleSupplier.id;
        setOffer(prev => ({ ...prev, items: updatedItems }));
        setVendors([]);
        setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
      } else if (suppliers.length > 1) {
        setVendors(suppliers);
        setShowVendorDropdown(prev => ({ ...prev, [index]: true }));
      } else {
        setVendors([]);
        setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
      }
    } catch (error) {
      console.error('Error fetching suppliers for product:', error);
      setVendors([]);
      setShowVendorDropdown(prev => ({ ...prev, [index]: false }));
    }
  };

  // After copying an offer, fetch suppliers for all imported items,
  // auto-select where possible, and queue remaining for user selection.
  const startSupplierSelectionFlow = useCallback(async (importedItems, startIndex) => {
    const autoSelections = {};
    const queue = [];

    await Promise.all(importedItems.map(async (item, i) => {
      if (!item.product_id) return;

      // The copied row already carries the supplier the source offer used, so
      // there is nothing to ask about. Previously every row was re-derived: a
      // product with several suppliers had its copied supplier WIPED (see the
      // queuedIndices loop below) and the user had to pick it again, and a
      // product with exactly one supplier — or any code starting with F — had
      // it silently overwritten.
      if (item.supplier_id) return;

      const productCode = (item.product || '').toUpperCase();
      const rowIndex = startIndex + i;
      // Not every source row carries a supplier *id* — rows coming from the
      // local `quotes` store can hold only the supplier code/name. Keying the
      // guard above on the id alone therefore still wiped those rows: they fell
      // through, got queued as "unresolved", and the user had to re-pick a
      // supplier the source offer plainly showed. Treat the name as just as
      // authoritative, and resolve it back to an id so the Zoho push carries
      // `Supplier: {id}` rather than nothing.
      const copiedName = String(item.supplier_name || '').trim();

      try {
        const response = await fetch(await withKey(`${API_BASE}/api/zoho/product_suppliers?product_id=${encodeURIComponent(item.product_id)}`));
        const data = await response.json();
        const suppliers = data.suppliers || [];

        if (copiedName) {
          // Exact match only, never a prefix or substring: `80DAYS` must not
          // resolve to `80DAYS80`. Code is tried before name and in its own
          // pass, so a supplier whose *code* matches always wins over one that
          // merely happens to carry the same string as its *name* — otherwise
          // the winner would depend on the order the cache returned them in.
          const wanted = copiedName.toUpperCase();
          const match =
            suppliers.find(s => (s.supplier_code || '').trim().toUpperCase() === wanted) ||
            suppliers.find(s => (s.name || '').trim().toUpperCase() === wanted);
          if (match) {
            autoSelections[rowIndex] = {
              // Trimmed: the comparison above ignores surrounding whitespace,
              // so storing the raw value would write back a code that no longer
              // compares equal to itself.
              supplier_name: (match.supplier_code || match.name || '').trim(),
              supplier_id: match.id
            };
          }
          // No match means the supplier is not linked to this product in the
          // cache. Keep what the source offer said rather than blanking it —
          // never queue a row that already names a supplier.
          return;
        }

        if (productCode.startsWith('F') && suppliers.length > 0) {
          const auto = suppliers.find(s =>
            (s.supplier_code || '').toUpperCase() === 'OCPHBILL' ||
            (s.name || '').toLowerCase() === 'billetkontoret'
          ) || suppliers[0];
          autoSelections[rowIndex] = {
            supplier_name: auto.supplier_code || auto.name,
            supplier_id: auto.id
          };
        } else if (suppliers.length === 1) {
          autoSelections[rowIndex] = {
            supplier_name: suppliers[0].supplier_code || suppliers[0].name,
            supplier_id: suppliers[0].id
          };
        } else if (suppliers.length > 1) {
          queue.push({ rowIndex, suppliers });
        }
      } catch (error) {
        console.error('Error fetching suppliers for copied item:', error);
      }
    }));

    // Apply the auto-selections. Queued rows are deliberately left untouched:
    // only a row with neither a supplier id nor a supplier name can reach the
    // queue now, so there is nothing to clear — and the loop that used to blank
    // `supplier_name` here is what stripped suppliers off copied rows.
    if (Object.keys(autoSelections).length > 0) {
      setOffer(prev => {
        const newItems = [...prev.items];
        for (const [idx, selection] of Object.entries(autoSelections)) {
          const i = Number(idx);
          if (newItems[i]) {
            newItems[i] = { ...newItems[i], ...selection };
          }
        }
        return { ...prev, items: newItems };
      });
    }

    // Sort queue top-to-bottom and start the selection flow
    queue.sort((a, b) => a.rowIndex - b.rowIndex);
    setPendingSupplierQueue(queue);
  }, [setOffer]);

  // Show vendor dropdown for the current head of the pending queue
  useEffect(() => {
    if (pendingSupplierQueue.length === 0) return;
    const { rowIndex, suppliers } = pendingSupplierQueue[0];
    // Avoid re-processing the same queue head
    if (pendingProcessedRef.current === rowIndex) return;
    pendingProcessedRef.current = rowIndex;
    setAllVendors(suppliers);
    setVendors(suppliers);
    setShowVendorDropdown(prev => ({ ...prev, [rowIndex]: true }));
  }, [pendingSupplierQueue]);

  // Reset processed ref when queue is empty
  useEffect(() => {
    if (pendingSupplierQueue.length === 0) {
      pendingProcessedRef.current = null;
    }
  }, [pendingSupplierQueue]);

  // Guards initDefaultProducts against running twice for the same offer.
  // React.StrictMode (src/index.js) deliberately double-invokes effects in
  // development — mount, cleanup, mount again — to surface exactly this kind
  // of bug. A ref survives that double-invocation because the component
  // instance itself isn't remounted, only the effect is re-run.
  const defaultProductsInitedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const offerId = params.get('offer_id');
    if (offerId) return;
    if (defaultProductsInitedRef.current) return;
    defaultProductsInitedRef.current = true;

    const initDefaultProducts = async () => {
      try {
        // Resolve the starter products by code, keeping the declared order.
        const resolved = await Promise.all(
          DEFAULT_PRODUCT_CODES.map(code => fetchProductByCode(code).catch(err => {
            console.error(`Error resolving starter product ${code}:`, err);
            return null;
          }))
        );
        const missing = DEFAULT_PRODUCT_CODES.filter((code, idx) => !resolved[idx]);
        if (missing.length > 0) {
          console.warn(`Starter products not found in cache: ${missing.join(', ')}. Run a bulk sync.`);
        }
        const defaultProducts = resolved.filter(Boolean);

        if (defaultProducts.length > 0) {
          const defaultItems = await Promise.all(defaultProducts.map(async (product, idx) => {
            let supplierName = '';
            let supplierId = null;

            try {
              const supplierResponse = await fetch(await withKey(`${API_BASE}/api/zoho/product_suppliers?product_id=${encodeURIComponent(product.id)}`));
              const supplierData = await supplierResponse.json();
              const suppliers = supplierData.suppliers || [];

              if (suppliers.length === 1) {
                supplierName = suppliers[0].supplier_code || suppliers[0].name;
                supplierId = suppliers[0].id;
              }
            } catch (err) {
              console.error(`Error fetching suppliers for product ${product.code}:`, err);
            }

            return {
              ...emptyItem(idx + 1),
              product: product.code || product.name,
              product_id: product.id,
              supplier_name: supplierName,
              supplier_id: supplierId,
              description: product.description || '',
              unit_cost: Number(product.unit_price || 0) || '',
              unit_price: Number(product.unit_price || 0) || '',
              vat: product.vat ? 1 : 0,
              hidden: true,
              product_hidden: !!product.hidden
            };
          }));

          setOffer(prev => {
            // The starter rows may already be sitting here — the ref guard
            // above should prevent that, but this check is what actually stops
            // a duplicate set from being inserted if this ever runs twice
            // regardless. isUntouchedStarterRow, not isStarterProductRow: once
            // the user has priced a starter row it is real work, so a second
            // run must not treat it as "already present" and skip topping it up
            // with the rest.
            const alreadyPresent = new Set(
              (prev.items || [])
                .filter(isUntouchedStarterRow)
                .map(i => String(i.product || '').trim().toUpperCase())
            );
            const toInsert = defaultItems.filter(
              item => !alreadyPresent.has(String(item.product || '').trim().toUpperCase())
            );
            if (toInsert.length === 0) return prev;

            // This runs after several awaited fetches, so the user may already
            // have typed something. Replacing prev.items outright (as this used
            // to) would silently delete that work — keep any real row and put
            // the starter rows after it.
            const typed = (prev.items || []).filter(isRowNonEmpty);
            if (typed.length === 0) {
              return { ...prev, items: toInsert };
            }
            const nextId = Math.max(0, ...typed.map(i => Number(i.id) || 0));
            return {
              ...prev,
              items: [
                ...typed,
                ...toInsert.map((item, idx) => ({ ...item, id: nextId + idx + 1 })),
              ],
            };
          });

          console.log(`Pre-populated ${defaultItems.length} starter products by code`);
        } else {
          console.warn('No starter products resolved by code — run a bulk sync.');
        }
      } catch (error) {
        console.error('Error fetching default products:', error);
      }
    };

    initDefaultProducts();
  }, [emptyItem, setOffer]);

  return {
    products,
    vendors,
    showProductDropdown,
    showVendorDropdown,
    searchingProduct,
    searchingVendor,
    setShowProductDropdown,
    setShowVendorDropdown,
    filterProducts,
    filterVendors,
    fetchAllProducts,
    fetchVendorsForProduct,
    selectProduct,
    selectVendor,
    pendingSupplierQueue,
    startSupplierSelectionFlow,
    advanceSupplierQueue
  };
}
